import {Button, Card, CardContent, CardHeader, colors, Grid, TextField, Theme, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/styles";
import React, {useEffect, useState} from "react";
import {getHandleFieldChange, getHasError} from "../../../../utils/formHelpers";
import {useSnackbar} from "notistack";
import clsx from "clsx";
import {RegionType, sortedCountries} from "../../../../utils/countries";
import {InternationalPhoneInput} from "../../../../components/PhoneInput/PhoneInput";
import validate from "validate.js";
import createBillingParty, {
    CreateBillingPartyInput
} from "../../../BillingParty/BillingPartyDetails/createBillingParty.api";
import useRouter from "use-react-router";

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: "100%",
        marginBottom: theme.spacing(2)
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        "&:hover": {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        "&:hover": {
            backgroundColor: theme.palette.error.main
        }
    },
    textField: {
        width: "100%"
    },
    phoneInputLabel: {
        display: "block",
        transform: "scale(0.75)",
        transformOrigin: "bottom left"
    },
    phoneInput: {
        "& label": {
            display: "none"
        }
    }
}));

const NewBillingParty = (props: {
    groupId: number;
    startDate: Date | string;
    endDate: Date | string;
    className?: string;
    onCancelClick?: () => void;
}) => {
    const {className, onCancelClick} = props;
    const classes = useStyles();
    const {history} = useRouter();
    const {enqueueSnackbar} = useSnackbar();
    const [sendCreateBillingParty, createData] = createBillingParty();
    const [formState, setFormState] = useState({
        first_name: "",
        last_name: "",
        email: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "",
        primary_phone: "",
        primary_phone_code: "",
        secondary_phone: "",
        secondary_phone_code: ""
    });
    const [regions, setRegions] = useState<RegionType[]>([]);
    const [errors, setErrors] = useState({
        isValid: false,
        roomAvailable: false,
        rangeInvalid: false,
        errors: {
            name: false,
            description: false,
            start_date: false,
            end_date: false,
            admissions_cutoff: false
        }
    });
    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    // 3. setup validation and error hook
    const validateSchema: any = {
        first_name: {
            presence: {allowEmpty: false, message: "is required"}
        },
        last_name: {
            presence: {allowEmpty: false, message: "is required"}
        },
        email: {
            presence: {allowEmpty: false, message: "is required"},
            email: true,
            length: {
                maximum: 64
            }
        },
        address_line_1: {
            presence: {allowEmpty: false, message: "is required"}
        },
        city: {
            presence: {allowEmpty: false, message: "is required"}
        },
        state: {
            presence: {allowEmpty: false, message: "is required"}
        },
        zipcode: {
            presence: {allowEmpty: false, message: "is required"}
        },
        country: {
            presence: {allowEmpty: false, message: "is required"}
        },
        primary_phone: {
            presence: {allowEmpty: false, message: "is required"},
            phone: true
        }
    };

    useEffect(() => {
        const selectedCountry = sortedCountries.find(
            country => country.countryShortCode === formState.country
        );
        if (selectedCountry) {
            if (regions !== selectedCountry.regions) {
                setRegions(selectedCountry.regions);
            }
        }
        const newErrors = validate(formState, validateSchema);

        setErrors(state => ({
            ...state,
            isValid: !newErrors,
            errors: newErrors || {}
        }));
    }, [formState]);

    useEffect(() => {
        const selectedCountry = sortedCountries.find(
            country => country.countryShortCode === formState.country
        );
        if (selectedCountry) {
            const newData = {
                ...formState,
                primary_phone_code: selectedCountry.phone,
                secondary_phone_code: selectedCountry.phone,
            }
            setFormState(newData)
        }
    }, [formState.country]);

    //4. Handle save form
    const onSaveClick = () => {
        const sanitisedData: CreateBillingPartyInput = {
            data: {
                ...{
                    id: undefined,
                    __typename: undefined,
                    group_id: props.groupId,
                    start_date: props.startDate,
                    end_date: props.endDate,
                    credit_card_fee: 0,
                    credit_card_fee_paid: undefined,
                    soft_cancelled: false,
                    disabled: false,
                    payments: undefined,
                    billing_party_member: {
                        ...formState,
                        billing_party_id: undefined,
                        __typename: undefined,
                        id: undefined,
                        billingParty: undefined,
                        user: undefined,
                        user_id: undefined,
                        payments: undefined,
                        primary_paying_member: true,
                        notes: '',
                        is_group_leader: false,
                    },
                },
            }
        };

        sendCreateBillingParty({variables: sanitisedData})
            .then((response) => {
                enqueueSnackbar('Booking Party Saved.', {variant: 'success'});
                history.push('/billing_party/' + response.data!.createBillingParty.id);
            })
            .catch(() => {
                enqueueSnackbar('Save Booking Party Failed!', {variant: 'error'});
            });
    };

    return (
        <Card className={clsx([className, classes.container])}>
            <CardHeader title="Create A New Billing Party"/>
            <CardContent className={classes.card}>
                <Grid container spacing={3}>
                    <Grid item md={6}>
                        <TextField
                            fullWidth
                            label="First Name*"
                            name="first_name"
                            onChange={handleFieldChange}
                            {...hasError("first_name")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item md={6}>
                        <TextField
                            fullWidth
                            label="Last Name*"
                            name="last_name"
                            onChange={handleFieldChange}
                            {...hasError("last_name")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item md={12}>
                        <TextField
                            fullWidth
                            label="Email*"
                            name="email"
                            onChange={handleFieldChange}
                            {...hasError("email")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item md={12}>
                        <TextField
                            fullWidth
                            select
                            label="County*"
                            name="country"
                            onChange={handleFieldChange}
                            {...hasError("country")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        >
                            <option value="">Please Select</option>
                            {sortedCountries.map(country => (
                                <option
                                    key={country.countryShortCode}
                                    value={country.countryShortCode}
                                >
                                    {country.countryName}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item md={12}>
                        <TextField
                            fullWidth
                            label="Billing Address 1*"
                            name="address_line_1"
                            onChange={handleFieldChange}
                            {...hasError("address_line_1")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item md={12}>
                        <TextField
                            fullWidth
                            label="Billing Address 2"
                            name="address_line_2"
                            onChange={handleFieldChange}
                            {...hasError("address_line_2")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item md={12}>
                        <TextField
                            fullWidth
                            label="City/Town*"
                            name="city"
                            onChange={handleFieldChange}
                            {...hasError("city")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item md={12}>
                        <TextField
                            fullWidth
                            select
                            label="State*"
                            name="state"
                            onChange={handleFieldChange}
                            {...hasError("state")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        >
                            <option value="">Please Select</option>
                            {regions.map(region => (
                                <option
                                    key={region.shortCode}
                                    value={region.shortCode}
                                >
                                    {region.name}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item md={12}>
                        <TextField
                            fullWidth
                            label="Zipcode*"
                            name="zipcode"
                            onChange={handleFieldChange}
                            {...hasError("zipcode")}
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="body1"
                            component="label"
                            className={classes.phoneInputLabel}
                        >
                            Phone Number (Primary)*
                        </Typography>
                        <InternationalPhoneInput
                            className={clsx(
                                classes.textField,
                                classes.phoneInput
                            )}
                            {...hasError("primary_phone")}
                            notched="true"
                            label={"Primary Phone"}
                            name="primary_phone"
                            type="text"
                            onChange={handleFieldChange}
                            onCodeChange={handleFieldChange}
                            variant="outlined"
                            codeValue={formState.primary_phone_code!}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="body1"
                            component="label"
                            className={classes.phoneInputLabel}
                        >
                            Phone Number (Secondary)*
                        </Typography>
                        <InternationalPhoneInput
                            className={clsx(
                                classes.textField,
                                classes.phoneInput
                            )}
                            {...hasError("secondary_phone")}
                            notched="true"
                            label={"Secondary Phone"}
                            name="secondary_phone"
                            type="text"
                            onChange={handleFieldChange}
                            onCodeChange={handleFieldChange}
                            variant="outlined"
                            codeValue={formState.secondary_phone_code!}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            className={classes.saveButton}
                            onClick={onSaveClick}
                            variant="contained"
                            disabled={!errors.isValid}
                        >
                            Save
                        </Button>
                        <Button variant={"outlined"} onClick={onCancelClick}>
                            Cancel
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default NewBillingParty;
